import * as _ from 'lodash';
import { useState } from 'react';
import { Alert, Button, DropdownButton, MenuItem } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../contexts/DialogContext';
import { orgRolesList, roleLabel } from '../../../helpers/roles';
import { useOnMount, useToggle } from '../../../hooks';
import { useRoles } from '../../../hooks/api';
import RolesModule from '../../../redux/roles';
import UsersModule, { type UsersStore } from '../../../redux/users';
import { hasError } from '../../../utils';
import { ErrorAlert, FormLabel, LabeledCheckbox, LabeledTextInput, TextInput } from '../../generic';
import { BUTTON_TYPE } from '../../generic/Button';
import CopyToClipboard from '../../generic/CopyToClipboard';
import Flex from '../../generic/Flex';
import Text from '../../generic/Text';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';
import OrganizationSettingsPermissionGate from '../OrganizationSettingsPermissionGate';
import { shouldDisplayApiUserTokenSettings } from '../shared/permissions';
import type { NewUser } from '../../../../shared/model-typings/user';
import type { ReduxStore } from '../../../redux/store';
import type { User } from '../../../types';

import styles from './apiTokens/apiTokens.module.less';

type ReduxStateProps = {
  usersStatus: UsersStore['status'];
};

type DispatchProps = {
  fetchRoles(): Promise<void>;
  createUser(
    user: NewUser & { createPushOnlyApiToken: boolean; createFullApiToken: boolean },
  ): Promise<{ value: { data: User & { pushOnlyApiToken?: string; fullApiToken?: string } } }>;
  updateUserRole(userId: number, roleId: number | null): Promise<void>;
};

type Props = DispatchProps & ReduxStateProps;

const CreateApiUserSettingsContent = (props: Props) => {
  const { onConfirm } = useDialogContext();

  const [username, setUsername] = useState<string>('');
  const [roleId, setRoleId] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [createPushOnlyApiToken, toggleCreatePushOnlyApiToken] = useToggle(true);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [createFullApiToken, toggleCreateFullApiToken] = useToggle(true);

  const { data: roles, error } = useRoles();
  const keyedRoles = _.keyBy(roles ?? [], (role) => role.id);

  // On mount, fetch all roles.
  useOnMount(props.fetchRoles);

  const onCreateUser = async () => {
    try {
      const { value } = await props.createUser({
        username,
        // role here has nothing to do with permissions but is just metadata about the user's role in their org.
        role: 'API user',
        createPushOnlyApiToken,
        createFullApiToken,
      });

      // If the new user's role isn't the role that was selected, update the user's role.
      if (value?.data && ((value.data as User).userRole?.roleId || null) !== roleId) {
        await props.updateUserRole(value.data.id, roleId);
      }

      onConfirm({
        title: 'New API User Created',
        onConfirm: _.noop,
        confirmBtnType: BUTTON_TYPE.PRIMARY,
        body: (
          <div>
            <p>
              A new API user has been created. Please copy{' '}
              {value.data.fullApiToken && value.data.pushOnlyApiToken ? 'these tokens' : 'the token'} below. The token
              {value.data.fullApiToken && value.data.pushOnlyApiToken ? 's' : ''} will never be displayed again.
            </p>
            {value.data.fullApiToken && (
              <div className={styles.tokenLabel}>
                <FormLabel label="Full API Token" />
                <TextInput
                  label="token-label"
                  value={value.data.fullApiToken}
                  suffix={<CopyToClipboard text={value.data.fullApiToken} />}
                />
              </div>
            )}
            {value.data.pushOnlyApiToken && (
              <div className={styles.tokenLabel}>
                <FormLabel label="Push-Only API Token" />
                <TextInput
                  label="token-label"
                  value={value.data.pushOnlyApiToken}
                  suffix={<CopyToClipboard text={value.data.pushOnlyApiToken} />}
                />
              </div>
            )}
          </div>
        ),
      });

      setUsername('');
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 2500);
    } catch (e) {
      // Error handled by Redux.
    }
  };

  return (
    <>
      {showSuccessMessage && <Alert bsStyle="success">New user created</Alert>}
      {error && <ErrorAlert status={{ errors: [error] }} />}
      {hasError(props.usersStatus) && <ErrorAlert status={props.usersStatus} />}
      <Header heading="API User" />
      <Section>
        <Section.Left heading="Create an API User">
          <Text.Text>
            Create a special user to have an API key that is unaffected by the organization&rsquo;s SSO configuration.
          </Text.Text>
        </Section.Left>
        <Section.Right>
          <LabeledTextInput label="Username" value={username} onChange={setUsername} />
          <FormLabel label="Role" htmlFor="new-user-org-role-dropdown">
            <DropdownButton
              id="new-user-org-role-dropdown"
              title={roleLabel(keyedRoles, roleId)}
              onSelect={setRoleId}
              bsStyle="default"
            >
              {orgRolesList(keyedRoles).map((role) => (
                <MenuItem key={role.id || 0} eventKey={role.id} active={roleId === role.id} name="roleId">
                  {role.name}
                </MenuItem>
              ))}
            </DropdownButton>
          </FormLabel>
          <LabeledCheckbox
            label="Create a push-only API token"
            isChecked={createPushOnlyApiToken}
            onChange={toggleCreatePushOnlyApiToken}
          />
          <br />
          <LabeledCheckbox
            label="Create a full API token"
            isChecked={createFullApiToken}
            onChange={toggleCreateFullApiToken}
          />
          <Flex justify="flex-end">
            <Button bsStyle="primary" onClick={onCreateUser} disabled={props.usersStatus.loading || !username.trim()}>
              Create
            </Button>
          </Flex>
        </Section.Right>
      </Section>
    </>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  usersStatus: UsersModule.selectors.getStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchRoles: RolesModule.actions.fetchAll,
  createUser: UsersModule.actions.create,
  updateUserRole: UsersModule.actions.updatePermission,
};

const CreateApiUserSettings = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateApiUserSettingsContent) as VoidFunctionComponent;

const CreateApiUserSettingsContainer = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayApiUserTokenSettings}>
    <CreateApiUserSettings />
  </OrganizationSettingsPermissionGate>
);

export default CreateApiUserSettingsContainer;
